import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  ArrowForward,
  ExpandMore,
  ExpandLess,
  PictureAsPdf,
  AccessTime,
  Person,
  CheckCircle,
  Cancel,
  Warning,
  Chat,
  Assignment,
  Payment,
  Description,
  InfoOutlined,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import SafeHtml from "@egovernments/digit-ui-module-common/src/sharedComponents/SafeHtml";

// ----- Helper: format epoch ms to "DD/MM/YYYY hh:mm A" -----
const formatDateTime = (epochMs) => {
  if (!epochMs) return "—";
  const date = new Date(epochMs);
  if (isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day}/${month}/${year} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
};

// ----- Helper: get epoch ms from a timeline item's auditDetails -----
const getItemEpoch = (item) =>
  item?.auditDetails?.lastModifieds ||
  item?.auditDetails?.lastModifiedTime ||
  0;

// ----- Helper: format milliseconds into days/hours/minutes -----
const formatDurationMs = (ms) => {
  if (!ms || ms < 0) return "< 1m";
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.length ? parts.join(" ") : "< 1m";
};

// ----- Helper: check if an action is a final (terminal) action -----
const isFinalAction = (action) => {
  if (!action) return false;
  const upper = action.toUpperCase();
  if (upper === "SHORTFALL" || upper === "REJECT" || upper === "REJECTED" || upper === "CANCEL") return true;
  if (upper === "APPROVED" || upper === "FIT_FOR_APPROVAL") return true;
  // Includes any APPROVAL action EXCEPT provisional
  if (upper.includes("APPROVAL") && upper !== "PROVISIONALLY_APPROVAL") return true;
  return false;
};

const AllRemarksList = ({ remarkDataResps, formData }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const user = window?.Digit?.UserService?.getUser()?.info || {};
  const isCitizenUser = user.type === "CITIZEN";

  const [filteredTimeline, setFilteredTimeline] = useState([]);
  const [expandedSubmissions, setExpandedSubmissions] = useState({});

  useEffect(() => {
    if (remarkDataResps?.timeline) {
      const fullyExcluded = ["MAKE_PAYMENT", "MAKE_PAYMENT_APPLICATION_FEE"];
      const seenStates = new Set();
      const filtered = remarkDataResps.timeline.filter((item) => {
        const state = typeof item?.state === "object" ? item?.state?.state : item?.state || "";
        if (fullyExcluded.includes(state)) return false;
        if (state === "UPLOAD_DOC") {
          if (seenStates.has("UPLOAD_DOC")) return false;
          seenStates.add("UPLOAD_DOC");
          return true;
        }
        if (state === "REVERT_TO_APPLICANT" && item?.assigner?.type === "CITIZEN") {
          return item?.wfComment?.length > 1 || item?.wfDocuments;
        }
        if (state === "REVERT_TO_APPLICANT" && item?.assigner?.type !== "CITIZEN") {
          return true;
        }
        return state !== "REVERT_TO_APPLICANT";
      });
      setFilteredTimeline(filtered);
    }
  }, [remarkDataResps]);

  const sortedTimeline = useMemo(() => {
    return [...filteredTimeline].sort(
      (a, b) => getItemEpoch(a) - getItemEpoch(b)
    );
  }, [filteredTimeline]);

  const breakActions = ["SHORTFALL", "REJECT", "CANCEL", "REVERT_TO_APPLICANT"];
  const submissions = useMemo(() => {
    const groups = [];
    let currentGroup = [];
    for (const item of sortedTimeline) {
      currentGroup.push(item);
      const action = item?.action || "";
      const state = typeof item?.state === "object" ? item?.state?.state : item?.state || "";
      if (breakActions.includes(action) || breakActions.includes(state)) {
        groups.push([...currentGroup]);
        currentGroup = [];
      }
    }
    if (currentGroup.length) groups.push(currentGroup);
    return groups;
  }, [sortedTimeline]);

  if (!submissions.length) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
        {t("NO_REMARKS_AVAILABLE")}
      </Typography>
    );
  }

  const SYSTEM_USER_NAMES = ["superuser", "system", "admin", "SYSTEM"];
  const getOfficerInfo = (user) => {
    if (!user) return { name: "—", role: "" };
    if (SYSTEM_USER_NAMES.includes(user.name?.toLowerCase?.() || user.userName?.toLowerCase?.())) {
      return { name: "—", role: "" };
    }
    const role =
      user.roles?.find((r) => !["EMPLOYEE", "CITIZEN", "OWNER", "WF_ADMIN_ESCLATE"].includes(r.code))?.name ||
      user.roles?.find((r) => r.code === "EMPLOYEE")?.name ||
      "";
    return { name: user.name || "—", role };
  };

  // ---- Render horizontal flow for a submission ----
  const renderSubmissionFlow = (items) => {
    const sortedItems = [...items].sort(
      (a, b) => getItemEpoch(a) - getItemEpoch(b)
    );

    const getCitizenLabel = (action) => {
      if (action === "INITIATE") return "Application Initiated";
      if (action === "UPLOAD_DOC") return null;
      if (action === "FORWARD_TO_DEPARTMENT") return "Submitted to Dept";
      if (action.includes("FORWARD") || action === "SEND_TO_NEXT_LEVEL") return "Forwarded";
      if (action.startsWith("PAYMENT_DONE") || action.includes("PAYMENT_DONE")) return "Payment Done";
      if (action == "MAKE_PAYMENT") return "Make Payment";
      return null;
    };

    const LTP_ROLES = ["BPA_SUPERVISOR", "BPA_Graduate_Civil_Engineer", "BPA_ARCHITECT", "LTP", "CITIZEN"];
    const ltpItem =
      sortedItems.find(
        (it) =>
          (it?.action === "INITIATE" || it?.performedAction === "INITIATE") &&
          it?.assigner?.roles?.some((r) => LTP_ROLES.includes(r.code))
      ) ||
      sortedItems.find((it) =>
        it?.assigner?.roles?.some((r) => LTP_ROLES.includes(r.code))
      );
    const ltpAssigner = ltpItem?.assigner || null;

    const steps = [];
    let hasPaymentTile = false;
    let hasMakePaymentTile = false;
    let provisionallyApprovalOfficer = null;

    for (let i = 0; i < sortedItems.length; i++) {
      const item = sortedItems[i];
      const assigner = item?.assigner;
      if (!assigner) continue;

      const rawAction = item.performedAction || item.action || "";
      const isPaymentAction = rawAction.startsWith("PAYMENT_DONE") || rawAction.includes("PAYMENT_DONE") || rawAction === "MAKE_PAYMENT";
      const isCitizenStep = assigner.type === "CITIZEN" || assigner.type === "OWNER" || isPaymentAction;
      let citizenLabel = getCitizenLabel(rawAction);
      const stateStr = typeof item?.state === "object" ? item?.state?.state : item?.state;
      if (rawAction === "PAYMENT_DONE" && stateStr === "FORWARD_TO_DEPARTMENT") {
        citizenLabel = "Payment Done & Forwarded";
      }

      if (isCitizenStep && !citizenLabel) continue;

      let itemAssignees = [];
      const rawAssign = item?.assignes || item?.assignees || item?.assignee;
      if (rawAssign) {
        itemAssignees = Array.isArray(rawAssign) ? rawAssign : [rawAssign];
      }
      if (isPaymentAction && itemAssignees.length === 0 && i + 1 < sortedItems.length) {
        const nextItem = sortedItems[i + 1];
        const nextRawAssign = nextItem?.assignes || nextItem?.assignees || nextItem?.assignee;
        if (nextRawAssign) {
          itemAssignees = Array.isArray(nextRawAssign) ? nextRawAssign : [nextRawAssign];
        }
      }

      if (isCitizenStep && citizenLabel?.includes("Payment Done")) {
        if (hasPaymentTile) {
          const existingStep = steps.find((s) => s.isCitizen && s.name?.includes("Payment Done"));
          if (existingStep && (!existingStep.assignees || existingStep.assignees.length === 0) && itemAssignees.length > 0) {
            existingStep.assignees = itemAssignees;
          }
          continue;
        }
        hasPaymentTile = true;
      }
      if (isCitizenStep && citizenLabel === "Make Payment") {
        if (hasMakePaymentTile) continue;
        hasMakePaymentTile = true;
      }

      let timeHeld = 0;
      if (i > 0) {
        const prevTime = getItemEpoch(sortedItems[i - 1]);
        const currTime = getItemEpoch(item);
        timeHeld = Math.max(currTime - prevTime, 0);
      }

      const inTime = i > 0 ? getItemEpoch(sortedItems[i - 1]) : getItemEpoch(item);
      const outTime = getItemEpoch(item);

      // Remark logic for citizens: show real remark only for final actions
      let realRemark = item?.comment || item?.wfComment?.[0] || "";
      let remark = realRemark;
      if (isCitizenUser) {
        if (!isFinalAction(rawAction)) {
          remark = ""; // hide remark for non-final steps
        }
      }

      if (isCitizenStep) {
        steps.push({
          name: citizenLabel,
          role: assigner.name || "Applicant",
          timeHeld,
          action: rawAction,
          itemIndex: i,
          assignerId: assigner.uuid || assigner.id,
          inTime,
          outTime,
          remark,
          isCitizen: true,
          assignees: itemAssignees,
        });
      } else {
        const { name, role } = getOfficerInfo(assigner);
        if (rawAction === "PROVISIONALLY_APPROVAL") {
          provisionallyApprovalOfficer = { name, role, assigner, outTime };
        }
        steps.push({
          name,
          role,
          timeHeld,
          action: rawAction,
          itemIndex: i,
          assignerId: assigner.uuid || assigner.id,
          inTime,
          outTime,
          remark,
          assignees: itemAssignees,
        });

        if (rawAction === "PROVISIONALLY_APPROVAL" && ltpAssigner) {
          const ltpRole =
            ltpAssigner.roles?.find((r) => r.code === "LTP")?.name ||
            ltpAssigner.roles?.find((r) => !["CITIZEN", "OWNER"].includes(r.code))?.name ||
            "LTP";

          const nextPaymentItem = sortedItems.slice(i + 1).find((it) => {
            const a = it?.action || it?.performedAction || "";
            return a.startsWith("PAYMENT_DONE") || a === "MAKE_PAYMENT" || a.includes("PAYMENT");
          });
          const ltpOutTime = nextPaymentItem ? getItemEpoch(nextPaymentItem) : null;
          const ltpTimeHeld = ltpOutTime ? Math.max(ltpOutTime - outTime, 0) : 0;

          steps.push({
            name: ltpAssigner.name || "—",
            role: ltpRole,
            timeHeld: ltpTimeHeld,
            action: "PENDING_PAYMENT_LTP",
            itemIndex: i,
            assignerId: ltpAssigner.uuid || ltpAssigner.id,
            inTime: outTime,
            outTime: ltpOutTime,
            remark: ltpOutTime ? "Payment done" : "Payment pending with LTP",
            isCitizen: true,
            isLtpPendingPayment: true,
            assignees: [],
          });
        }
      }
    }

    const lastItem = sortedItems[sortedItems.length - 1];
    const lastAction = lastItem?.performedAction || lastItem?.action || "";
    const lastStateStr = typeof lastItem?.state === "object" ? lastItem?.state?.state : lastItem?.state || "";
    const isDscTerminal = lastAction === "FIT_FOR_APPROVAL";
    const isDscApply = lastAction.startsWith("APPLY_FOR_DSC");
    let finalStatus = null;
    if ((lastAction.includes("APPROVAL") && lastAction !== "PROVISIONALLY_APPROVAL") || lastAction === "APPROVED" || isDscTerminal) {
      finalStatus = "APPROVED";
    } else if (lastAction.includes("REJECT") || lastAction === "REJECT") {
      finalStatus = "REJECTED";
    } else if (lastAction === "SHORTFALL") {
      finalStatus = "SHORTFALL";
    }

    const tileData = [...steps];
    const lastStep = steps[steps.length - 1];
    const isLastPayment = lastStep?.action?.startsWith("PAYMENT_DONE") || lastStep?.action === "MAKE_PAYMENT";

    const shouldAddCurrentHolder = !finalStatus && lastStep && lastStep.assignees?.length > 0 && !isLastPayment;

    if (shouldAddCurrentHolder) {
      const holder = lastStep.assignees[0];
      const holderName = typeof holder === "string" ? holder : (holder?.name || holder?.userName || "—");
      const holderRole = holder?.roles?.find((r) => !["EMPLOYEE", "CITIZEN", "OWNER"].includes(r.code))?.name || "";
      tileData.push({
        name: holderName,
        role: holderRole,
        timeHeld: 0,
        action: "",
        inTime: lastStep.outTime,
        outTime: null,
        remark: "",
        assignees: [],
        isCurrentHolder: true,
      });
    } else if (!finalStatus && isLastPayment && provisionallyApprovalOfficer) {
      tileData.push({
        name: provisionallyApprovalOfficer.name,
        role: provisionallyApprovalOfficer.role,
        timeHeld: 0,
        action: "",
        inTime: lastStep.outTime,
        outTime: null,
        remark: isCitizenUser ? "Pending Approval" : "File pending with officer",
        assignees: [],
        isCurrentHolder: true,
      });
    } else if (isDscApply && !finalStatus) {
      const dscAssigner = lastItem?.assigner;
      if (dscAssigner) {
        const { name: dscName, role: dscRole } = getOfficerInfo(dscAssigner);
        tileData.push({
          name: dscName || "—",
          role: dscRole,
          timeHeld: 0,
          action: "",
          inTime: lastStep?.outTime,
          outTime: null,
          remark: "",
          assignees: [],
          isCurrentHolder: true,
        });
      }
    } else if (finalStatus) {
      tileData.push({
        isFinal: true,
        status: finalStatus,
        timeHeld: 0,
        name: finalStatus,
        role: "",
        action: lastAction,
      });
    }

    if (steps.length === 0 && tileData.filter(t => !t.isFinal).length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {t("NO_OFFICER_DATA")}
        </Typography>
      );
    }

    // ---------- Tile rendering ----------
    const renderTile = (step, index) => {
      const isFinal = step.isFinal || false;
      const isCitizen = step.isCitizen || false;
      const isCurrentHolder = step.isCurrentHolder || false;
      const timeStr = formatDurationMs(step.timeHeld || 0);
      let icon = <Person />;
      let bgColor = theme.palette.primary.main;
      let tileBg = theme.palette.background.paper;
      let borderColor = theme.palette.divider;

      if (isCurrentHolder) {
        bgColor = "#00897b";
        tileBg = "#e0f2f1";
        borderColor = "#00897b";
        icon = <Person />;
      } else if (isCitizen) {
        const act = step.action || "";
        if (act === "INITIATE") icon = <Assignment />;
        else if (act.includes("PAYMENT")) icon = <Payment />;
        else icon = <Description />;
        bgColor = theme.palette.warning.dark;
        tileBg = "#fff8e1";
        borderColor = theme.palette.warning.main;
      } else if (isFinal) {
        if (step.status === "APPROVED") {
          icon = <CheckCircle />;
          bgColor = theme.palette.success.main;
          tileBg = theme.palette.success.light;
          borderColor = theme.palette.success.main;
        } else if (step.status === "REJECTED") {
          icon = <Cancel />;
          bgColor = theme.palette.error.main;
          tileBg = theme.palette.error.light;
          borderColor = theme.palette.error.main;
        } else if (step.status === "SHORTFALL") {
          icon = <Warning />;
          bgColor = theme.palette.warning.main;
          tileBg = theme.palette.warning.light;
          borderColor = theme.palette.warning.main;
        }
      }

      return (
        <Box key={index} sx={{ display: "flex", alignItems: "center", mx: 1, my: 2 }}>
          <Paper
            elevation={1}
            sx={{
              position: "relative",
              p: 1.5,
              borderRadius: 2,
              minWidth: 120,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: tileBg,
              border: `1px solid ${borderColor}`,
            }}
          >
            {step.assignees?.length > 0 && (
              <Tooltip
                title={
                  <Box sx={{ p: 0.5, lineHeight: 1.9 }}>
                    <div style={{ fontWeight: "bold", marginBottom: 2 }}>Assigned To</div>
                    {step.assignees.map((a, idx) => {
                      const assigneeName = typeof a === "string" ? a : (a?.name || a?.userName || "—");
                      const assigneeRole = a?.roles?.find((r) => !["EMPLOYEE", "CITIZEN", "OWNER"].includes(r.code))?.name || "";
                      return (
                        <div key={idx}>
                          <div>👤 {assigneeName}{assigneeRole ? ` (${assigneeRole})` : ""}</div>
                          {!isCitizenUser && a?.mobileNumber && <div>📞 {a.mobileNumber}</div>}
                          {idx < step.assignees.length - 1 && <div style={{ borderTop: "1px solid rgba(255,255,255,0.3)", margin: "4px 0" }} />}
                        </div>
                      );
                    })}
                  </Box>
                }
                arrow
                placement="top"
              >
                <InfoOutlined
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    fontSize: 16,
                    color: "info.main",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            )}
            <Avatar sx={{ width: 40, height: 40, bgcolor: bgColor }}>
              {icon}
            </Avatar>
            <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.5 }}>
              {step.name}
            </Typography>
            {!isFinal && (
              <Typography variant="caption" color="text.secondary">
                {step.role}
              </Typography>
            )}
            {!isFinal && (() => {
              const act = step.action || "";
              const isPaymentTile = act !== "PENDING_PAYMENT_LTP" && (act.startsWith("PAYMENT_DONE") || act === "MAKE_PAYMENT" || act.includes("PAYMENT"));
              if (isPaymentTile) return null;
              return (
                <Tooltip
                  title={
                    <Box sx={{ p: 0.5, lineHeight: 1.8 }}>
                      <div><b>In:</b>&nbsp;{step.inTime ? formatDateTime(step.inTime) : "—"}</div>
                      <div><b>Out:</b>&nbsp;{isCurrentHolder ? "Active (file pending)" : step.outTime ? formatDateTime(step.outTime) : "—"}</div>
                      {!isCurrentHolder && <div><b>Held:</b>&nbsp;{formatDurationMs(step.timeHeld || 0)}</div>}
                      {isCurrentHolder && <div style={{ color: "#80cbc4", fontStyle: "italic" }}>File is currently with this officer</div>}
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <Typography
                    variant="caption"
                    color={isCurrentHolder ? "success.dark" : "text.secondary"}
                    sx={{ display: "flex", alignItems: "center", mt: 0.5, cursor: "pointer", fontWeight: isCurrentHolder ? "bold" : "normal" }}
                  >
                    <AccessTime fontSize="small" sx={{ mr: 0.5 }} />
                    {isCurrentHolder ? "Active" : timeStr}
                  </Typography>
                </Tooltip>
              );
            })()}
            {!isFinal && (
              <Tooltip
                title={
                  step.remark ? (
                    <Box sx={{ p: 0.5, maxWidth: 240, wordBreak: "break-word" }}>
                      <SafeHtml html={step.remark} />
                    </Box>
                  ) : (
                    <Box sx={{ p: 0.5, fontStyle: "italic" }}>No remark</Box>
                  )
                }
                arrow
                placement="bottom"
              >
                <Chat
                  fontSize="small"
                  sx={{
                    mt: 0.5,
                    cursor: "pointer",
                    color: step.remark ? "primary.main" : "text.disabled",
                  }}
                />
              </Tooltip>
            )}
          </Paper>
        </Box>
      );
    };

    const renderArrow = (fromIdx, toIdx) => {
      const fromStep = tileData[fromIdx];
      const action = fromStep.action || "";
      const FORWARD_ACTIONS = [
        "INITIATE",
        "UPLOAD_DOC",
        "FORWARD_TO_DEPARTMENT",
        "SEND_TO_NEXT_LEVEL",
        "PROVISIONALLY_APPROVAL",
        "FIT_FOR_APPROVAL",
        "APPLY_FOR_DSC",
        "APPLY_FOR_DSC_VC",
        "APPLY_FOR_DSC_EE_TP",
      ];
      const isForward =
        FORWARD_ACTIONS.includes(action) ||
        action.includes("FORWARD") ||
        action.includes("NEXT_LEVEL") ||
        action.includes("APPROVAL") ||
        action.startsWith("PAYMENT_DONE") ||
        action.includes("PAYMENT") ||
        action === "APPROVED" ||
        action === "MAKE_PAYMENT";
      const color = isForward ? "success.main" : "error.main";
      return (
        <Box sx={{ display: "flex", alignItems: "center", mx: 0.5 }}>
          <ArrowForward sx={{ color, fontSize: 30 }} />
        </Box>
      );
    };

    return (
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", py: 1 }}>
        {tileData.map((step, idx) => (
          <React.Fragment key={idx}>
            {renderTile(step, idx)}
            {idx < tileData.length - 1 && renderArrow(idx, idx + 1)}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  // ---- Cumulative remarks table ----
  const renderRemarksTable = (items) => {
    // Remove duplicate UPLOAD_DOC rows
    let displaySteps = [];
    let seenUploadDoc = false;
    for (const item of items) {
      const performedAction = item?.performedAction || item?.action || "";
      if (performedAction === "UPLOAD_DOC") {
        if (seenUploadDoc) continue;
        seenUploadDoc = true;
      }
      displaySteps.push(item);
    }

    if (displaySteps.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {t("NO_REMARKS")}
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("WF_PERFORMED_ACTION") || "Action"}</TableCell>
              <TableCell>{t("WF_STATUS") || "Status"}</TableCell>
              <TableCell>{t("WF_COMMON_REMARKS")}</TableCell>
              <TableCell>{t("WF_COMMON_DATE")}</TableCell>
              {/* Hide entire documents column for citizens */}
              {!isCitizenUser && (
                <TableCell>{t("CS_COMMON_ATTACHMENTS")}</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {displaySteps.map((item, idx) => {
              const { name, role } = getOfficerInfo(item?.assigner);
              const performedAction = item?.performedAction || item?.action || "—";
              const statusVal = item?.status || item?.state || "—";
              const docs = item?.wfDocuments || item?.documents || [];
              const hasDocs = docs.length > 0;
              const displayName = SYSTEM_USER_NAMES.includes(item?.assigner?.name?.toLowerCase?.() || item?.assigner?.userName?.toLowerCase?.()) ? "—" : name;

              // Citizen remarks: show real comment only for final actions, else "—"
              const showRealRemarkForCitizen = isCitizenUser && isFinalAction(performedAction);
              const citizenRemarkContent = showRealRemarkForCitizen ? (item?.comment || item?.wfComment?.[0] || "") : null;

              return (
                <TableRow key={idx}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">{t(performedAction) || performedAction}</Typography>
                      {(displayName && displayName !== "—") && (
                        <Typography variant="caption" color="text.secondary">{displayName}{role ? ` (${role})` : ""}</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{t(statusVal) || statusVal}</Typography>
                  </TableCell>
                  <TableCell>
                    {isCitizenUser ? (
                      showRealRemarkForCitizen ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <SafeHtml html={citizenRemarkContent} style={{ fontSize: "0.875rem", lineHeight: 1.5 }} />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )
                    ) : (
                      (() => {
                        const remarkParts = [];
                        if (item?.comment) remarkParts.push({ label: null, text: item.comment });
                        if (item?.documentsRemarks) remarkParts.push({ label: "Documents", text: item.documentsRemarks });
                        if (item?.paymentRemarks) remarkParts.push({ label: "Payment", text: item.paymentRemarks });
                        if (item?.drawingRemarks) remarkParts.push({ label: "Drawing", text: item.drawingRemarks });
                        if (item?.inspectionRemarks) remarkParts.push({ label: "Inspection", text: item.inspectionRemarks });
                        return remarkParts.length > 0 ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {remarkParts.map((part, pi) => (
                              <Box key={pi} sx={{ display: "flex", gap: 0.5, alignItems: "flex-start" }}>
                                {part.label && (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    fontWeight="bold"
                                    color="text.secondary"
                                    sx={{ whiteSpace: "nowrap", pt: "2px" }}
                                  >
                                    {part.label}:
                                  </Typography>
                                )}
                                <SafeHtml html={part.text} style={{ fontSize: "0.875rem", lineHeight: 1.5 }} />
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        );
                      })()
                    )}
                  </TableCell>
                  <TableCell>{formatDateTime(getItemEpoch(item))}</TableCell>
                  {/* Documents cell only for non-citizens */}
                  {!isCitizenUser && (
                    <TableCell>
                      {hasDocs ? (
                        docs.map((doc, i) => (
                          <Tooltip key={i} title={doc?.documentType || "Attachment"}>
                            <PictureAsPdf
                              sx={{ cursor: "pointer", color: "error.main", fontSize: 25, mr: 1 }}
                              onClick={() => window.open(doc?.url || doc?.fileStoreId, "_blank")}
                            />
                          </Tooltip>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const toggleSubmission = (index) => {
    setExpandedSubmissions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const reversedSubmissions = [...submissions].reverse();
  const latestOriginalIdx = submissions.length - 1;

  return (
    <Box sx={{ mt: 2 }}>
      {reversedSubmissions.map((group, renderedIdx) => {
        const originalIdx = latestOriginalIdx - renderedIdx;
        const submissionNumber = originalIdx + 1;
        const isExpanded = expandedSubmissions[originalIdx] !== undefined
          ? expandedSubmissions[originalIdx]
          : renderedIdx === 0;
        const submissionLabel = `Submission ${submissionNumber}`;
        const firstItem = group[0];
        const lastItem = group[group.length - 1];
        const totalDuration = formatDurationMs(
          getItemEpoch(lastItem) - getItemEpoch(firstItem)
        );

        return (
          <Paper key={originalIdx} elevation={2} sx={{ mb: 3, p: 2, borderRadius: 2 }}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h6" fontWeight="bold">
                  {submissionLabel}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    {t("WF_TIME_TAKEN")}: {totalDuration}
                  </Typography>
                </Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={() => toggleSubmission(originalIdx)}>
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Grid>
            </Grid>

            <Collapse in={isExpanded}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ overflowX: "auto", py: 1 }}>{renderSubmissionFlow(group)}</Box>
              {renderRemarksTable(group)}
            </Collapse>
          </Paper>
        );
      })}
    </Box>
  );
};

export default AllRemarksList;